import { useState, useEffect, useCallback } from 'react';
import { App } from '@capacitor/app';
import logger from '@/lib/logger';
import { isCapacitor, getBaseUrl, APP_CONFIG, TIMEOUTS } from '@/lib/config';

// VersiÃ³n actual de la app (desde configuraciÃ³n centralizada)
export const APP_VERSION = APP_CONFIG.version;
export const APP_VERSION_CODE = APP_CONFIG.versionCode;

interface VersionInfo {
  version: string;
  versionCode: number;
  releaseDate: string;
  downloadUrl: string;
  changelog: string[];
  minVersion: string;
  forceUpdate: boolean;
}

interface UpdateState {
  isChecking: boolean;
  hasUpdate: boolean;
  isForceUpdate: boolean;
  versionInfo: VersionInfo | null;
  error: string | null;
}

export function useAppUpdate() {
  const [state, setState] = useState<UpdateState>({
    isChecking: false,
    hasUpdate: false,
    isForceUpdate: false,
    versionInfo: null,
    error: null,
  });
  const [runtimeVersion, setRuntimeVersion] = useState(APP_VERSION);
  const [runtimeVersionCode, setRuntimeVersionCode] = useState(APP_VERSION_CODE);

  useEffect(() => {
    if (!isCapacitor) return;

    App.getInfo()
      .then((info) => {
        if (info?.version) {
          setRuntimeVersion(info.version);
        }
        const buildCode = Number(info?.build);
        if (!Number.isNaN(buildCode)) {
          setRuntimeVersionCode(buildCode);
        }
      })
      .catch((error) => {
        logger.warn('Error reading native app info:', error);
      });
  }, []);

  const checkForUpdates = useCallback(async () => {
    // Solo verificar en Capacitor (app nativa)
    if (!isCapacitor) {
      return;
    }

    setState(prev => ({ ...prev, isChecking: true, error: null }));

    try {
      const serverUrl = getBaseUrl();
      const response = await fetch(`${serverUrl}/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error('No se pudo verificar actualizaciones');
      }

      const versionInfo: VersionInfo = await response.json();
      
      // Comparar versiones
      const hasUpdate = versionInfo.versionCode > runtimeVersionCode;
      const isForceUpdate = hasUpdate && versionInfo.forceUpdate;

      setState({
        isChecking: false,
        hasUpdate,
        isForceUpdate,
        versionInfo: hasUpdate ? versionInfo : null,
        error: null,
      });

      return { hasUpdate, isForceUpdate, versionInfo };
    } catch (error: any) {
      logger.warn('Error checking for updates:', error);
      setState(prev => ({
        ...prev,
        isChecking: false,
        error: error.message,
      }));
      return null;
    }
  }, [runtimeVersionCode]);

  const downloadUpdate = useCallback(() => {
    if (!state.versionInfo?.downloadUrl) return;

    const serverUrl = getBaseUrl();
    const downloadUrl = state.versionInfo.downloadUrl.startsWith('http')
      ? state.versionInfo.downloadUrl
      : `${serverUrl}${state.versionInfo.downloadUrl}`;

    // Abrir el link de descarga
    window.open(downloadUrl, '_system');
  }, [state.versionInfo]);

  const dismissUpdate = useCallback(() => {
    if (state.isForceUpdate) return; // No permitir descartar actualizaciones forzadas
    
    setState(prev => ({
      ...prev,
      hasUpdate: false,
      versionInfo: null,
    }));

    // Guardar que el usuario descartÃ³ esta versiÃ³n
    if (state.versionInfo) {
      localStorage.setItem('dismissed_version', state.versionInfo.version);
    }
  }, [state.isForceUpdate, state.versionInfo]);

  // Verificar al iniciar la app
  useEffect(() => {
    // Solo en Capacitor
    if (!isCapacitor) return;

    // Esperar un poco antes de verificar para no bloquear el inicio
    const timeout = setTimeout(() => {
      // Verificar si el usuario ya descartÃ³ esta actualizaciÃ³n
      const dismissedVersion = localStorage.getItem('dismissed_version');
      checkForUpdates().then(result => {
        if (result?.versionInfo && result.versionInfo.version === dismissedVersion && !result.isForceUpdate) {
          setState(prev => ({
            ...prev,
            hasUpdate: false,
            versionInfo: null,
          }));
        }
      });
    }, 2000);

    return () => clearTimeout(timeout);
  }, [checkForUpdates]);

  return {
    ...state,
    currentVersion: runtimeVersion,
    currentVersionCode: runtimeVersionCode,
    checkForUpdates,
    downloadUpdate,
    dismissUpdate,
    isNativeApp: isCapacitor,
  };
}





